import * as React from "react";
import { Button, TextBox, TreeList } from "devextreme-react";
import { Column, Selection } from "devextreme-react/tree-list";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IGoedePraktijkPublicData } from "../models/IGoedePraktijk";
import { IOperationeleDoelstelling } from "../models/OperationeleDoelstelling";
import { useFetchData, useListSource } from "../components/hooks/DataSource";
import useLocationState from "../components/hooks/LocationState";
import ValueCheckBox from "../components/ValueCheckBox";
import { IApiUrls } from "../models/IApiUrls";
import { calcSize } from "../components/utils/FileUtils"
import { IListsModel } from "../models/IHomeModel";
import { IThema } from "../models/IThema";
import { IPageLoading } from "../App";

interface IProps extends IPageLoading {
    apiUrls: IApiUrls;
    lists: IListsModel;    
}

interface IFormData {
    trefwoord: string;
    themas: number[];
    operationeleDoelstellingen: number[];
}

interface ILocationState {
    formData: IFormData;
    search?: boolean;
}

const ZoekGoedePraktijk = ({ apiUrls, lists, setPageLoading }: IProps) => {

    const resultsRef = useRef<HTMLDivElement>(null);
    const dsOpDoel = useListSource(lists.doelstellingen);
    const [{ formData, search }, setFormData] = useLocationState<ILocationState>({ formData: { operationeleDoelstellingen: [], themas: [], trefwoord: "" } });
    const fetcher = useFetchData();
    const [data, setData] = useState<IGoedePraktijkPublicData[]>([])
    const [scrollEnabled, setScrollEnabled] = useState(false);

    useEffect(() => {        
        if (search) {            
            setPageLoading(true);
            fetcher(apiUrls.zoekGoedePraktijk, formData, 'POST').then(data => {
                setData(data);
                scrollEnabled && resultsRef.current?.scrollIntoView({ behavior: "smooth" });
                setPageLoading(false);
            }).catch(() => setPageLoading(false));
        }
    }, [formData, fetcher, apiUrls.zoekGoedePraktijk, setPageLoading, search, scrollEnabled])

    const trefwoordChangeHandler = useCallback((newValue: string) =>
        setFormData(prev => ({ search: false, formData: { ...prev.formData, trefwoord: newValue } })), [setFormData])

    const doelstellingChangeHandler = useCallback((newValue: number[]) =>
        setFormData(prev => ({ search: false, formData: { ...prev.formData, operationeleDoelstellingen: newValue } })), [setFormData])

    const onThemaCheckChangedHandler = useCallback((value: any, checked: boolean) => {        
        setFormData(prev => ({
            search: false,
            formData: {
                ...prev.formData,
                themas: (checked && ([...prev.formData.themas!, value])) || prev.formData.themas!.filter(p => p !== value)
            }
        }));
    }, [setFormData])

    const seacrClickHandler = useCallback(() => {
        setScrollEnabled(true);
        setFormData(prev => ({ ...prev, search: true }));
    }, [setFormData])

    return <>
        <div className="form-group">
            <label>Trefwoorden</label>
            <TextBox value={formData.trefwoord} onValueChange={trefwoordChangeHandler} />
        </div>

        <div className="form-group">
            <label>Thema</label>
            <div className="flex-row wrap">
                {lists.themas?.map((p, idx) => <div key={idx} className="flex-col3 checkitem">
                    <ValueCheckBox text={p.naam} value={p.id} checked={formData.themas?.includes(p.id) || false} onCheckChanged={onThemaCheckChangedHandler} />
                </div>)}
            </div>
        </div>

        <div className="form-group">
            <label>Operationele doelstellingen</label>
            <TreeList<IOperationeleDoelstelling, number>
                dataSource={dsOpDoel}
                parentIdExpr="hoofdDoelstellingId"
                rootValue={undefined}
                onSelectedRowKeysChange={doelstellingChangeHandler}
                selectedRowKeys={formData.operationeleDoelstellingen}
                showColumnHeaders={false}
                autoExpandAll
            >
                <Selection mode="multiple" recursive />
                <Column dataField="naam" />
            </TreeList>
        </div>
        <div className="form-group">
            <Button text="Zoeken" onClick={seacrClickHandler} type="default" icon="search" />
        </div>
        <div id="results" ref={resultsRef}>
            {search && <h2>Resultaten:</h2>}
            {search && data.length === 0 && <p>Geen resultaten</p>}
            {data.map((gp, idx) => <GoedePraktijkItem key={idx} goedePraktijk={gp} />)}
        </div>
    </>
}

const GoedePraktijkItem = ({ goedePraktijk }: { goedePraktijk: IGoedePraktijkPublicData }) => {

    const linkLabel = useMemo(() => (
        `Download rapport ${goedePraktijk.visitatieRapportDocument.fileName} - ${calcSize(goedePraktijk.visitatieRapportDocument.size)}`
    ), [goedePraktijk.visitatieRapportDocument])

    return <div className="goedepraktijk">
        <h4 className="bullet">{goedePraktijk.titel}</h4>
        <div dangerouslySetInnerHTML={{ __html: goedePraktijk.htmlOmschrijving }} />
        <strong>Documenten</strong>
        <p>
            <a href={"/api/document/" + goedePraktijk.visitatieRapportDocument.id} >
                <i className="dx-icon dx-icon-pdffile" />
                {linkLabel}
            </a>
        </p>
        <strong>Thema</strong>
        {/*<p>{goedePraktijk.themas.join(', ')}</p>*/}
        <div className="themaItems" >
            {goedePraktijk.themas.map((thema, idx) => <ThemaItem key={idx} text={thema} />)}
        </div>
    </div>
}

const ThemaItem = ({ text }: { text?: string }) => {

    return <div className="themaItem">
        <span>{text}</span>
    </div>;
}

export default ZoekGoedePraktijk;